import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AnimatedLogo } from '@/components/ui/AnimatedLogo';
import { 
    MapPin, 
    Phone, 
    Mail, 
    Instagram, 
    Facebook, 
    Youtube, 
    Twitter,
    Send,
    Heart,
    Globe,
    Music2
} from 'lucide-react';

const footerLinks = {
    navigation: [
        { name: 'Home', href: '/' },
        { name: 'About Us', href: '/about' },
        { name: 'Performances', href: '/performances' },
        { name: 'Gallery', href: '/gallery' },
        { name: 'Reviews', href: '/reviews' },
    ],
    services: [
        { name: 'Wedding Events', href: '/book' },
        { name: 'Corporate Shows', href: '/book' },
        { name: 'Private Parties', href: '/book' },
        { name: 'Concert Tours', href: '/book' },
        { name: 'Music Festivals', href: '/book' },
    ],
};

const socialLinks = [
    { icon: Instagram, href: 'https://instagram.com/hawayeintheband', label: 'Instagram' },
    { icon: Facebook, href: 'https://facebook.com/hawayeintheband', label: 'Facebook' },
    { icon: Youtube, href: 'https://youtube.com/@hawayeintheband', label: 'YouTube' },
    { icon: Twitter, href: 'https://twitter.com/hawayeinband', label: 'Twitter' },
];

export const Footer = () => {
    const handleNewsletterSubmit = (e) => {
        e.preventDefault();
        alert('Thank you for subscribing to our newsletter!');
    };

    return (
        <footer className="relative bg-card/50 border-t border-border/30 overflow-hidden">
            {/* Subtle Spotlight Effect */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
            
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
                    {/* Brand Section */}
                    <div className="lg:col-span-1 space-y-6">
                        <Link to="/">
                            <AnimatedLogo size="default" showText={true} />
                        </Link>
                        <p className="text-muted-foreground text-sm leading-relaxed font-body">
                            Creating unforgettable musical experiences across India. Let the music take you on a journey you'll never forget.
                        </p>
                        
                        {/* Website Link */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Globe className="w-4 h-4 text-primary" />
                            <a 
                                href="https://hawayeinband.in" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="hover:text-primary transition-colors"
                            >
                                hawayeinband.in
                            </a>
                        </div>
                        
                        {/* Social Links */}
                        <div className="flex gap-3">
                            {socialLinks.map((social) => (
                                <motion.a
                                    key={social.label}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    whileHover={{ scale: 1.1, y: -2 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="w-10 h-10 rounded-lg bg-muted/50 border border-border/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors duration-200"
                                    aria-label={social.label}
                                >
                                    <social.icon className="w-4 h-4" />
                                </motion.a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-5">
                        <h4 className="font-heading font-semibold text-foreground text-base">
                            Quick Links
                        </h4>
                        <ul className="space-y-3">
                            {footerLinks.navigation.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.href}
                                        className="text-muted-foreground hover:text-primary text-sm font-body transition-colors duration-200"
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Services */}
                    <div className="space-y-5">
                        <h4 className="font-heading font-semibold text-foreground text-base">
                            Our Services
                        </h4>
                        <ul className="space-y-3">
                            {footerLinks.services.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.href}
                                        className="text-muted-foreground hover:text-primary text-sm font-body transition-colors duration-200"
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact & Newsletter */}
                    <div className="space-y-6">
                        <h4 className="font-heading font-semibold text-foreground text-base">
                            Get In Touch
                        </h4>
                        
                        <div className="space-y-3">
                            <div className="flex items-start gap-3 text-sm">
                                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                <div className="text-muted-foreground font-body">
                                    <p className="font-medium text-foreground mb-1">Office:</p>
                                    <p>Wallfort City Road, Grandiose Residence,</p>
                                    <p>Suite No. A117, Bhathagaon,</p>
                                    <p>Raipur, Chhattisgarh 492001</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 text-sm">
                                <Music2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                                <div className="text-muted-foreground font-body">
                                    <p className="font-medium text-foreground mb-1">Jamming Studio:</p>
                                    <p>Near Radha Krishna Temple,</p>
                                    <p>Santoshi Nagar, Raipur,</p>
                                    <p>Chhattisgarh 492001</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                                <a href="tel:+917000312302" className="text-muted-foreground hover:text-primary font-body transition-colors">
                                    +91 7000312302
                                </a>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                                <a href="mailto:musicalyash92@gmail.com" className="text-muted-foreground hover:text-primary font-body transition-colors">
                                    musicalyash92@gmail.com
                                </a>
                            </div>
                        </div>

                        {/* Newsletter */}
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground font-body">
                                Subscribe for updates & exclusive offers
                            </p>
                            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                                <Input
                                    type="email"
                                    placeholder="Your email"
                                    className="bg-muted/50 border-border/50 text-foreground text-sm flex-1"
                                    required
                                />
                                <Button
                                    type="submit"
                                    size="icon"
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                                >
                                    <Send className="w-4 h-4" />
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-14 pt-8 border-t border-border/30">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-muted-foreground font-body text-center md:text-left">
                            © {new Date().getFullYear()} Hawayein The Band. All rights reserved.
                        </p>
                        <p className="text-sm text-muted-foreground font-body flex items-center gap-1.5">
                            Made with <Heart className="w-3.5 h-3.5 text-destructive fill-destructive" /> for music lovers
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};
